import { useEffect, useState } from 'react'
import studentRequest from 'utils/studentRequest'

const UNLOCKED = {
  status: 'NONE',
  isLocked: false,
  message: null,
  endDate: null,
  daysToExpiry: null,
  expiringSoon: false,
  reportsLocked: false,
  lockedTypes: [],
}

/**
 * The student's institute subscription state — the contract window behind the
 * locked assessment cards.
 *
 * Fetched separately from the assessment list because the banner has to render
 * even when that list comes back empty, which an expired contract can produce.
 * Any failure resolves to "unlocked": the real enforcement is server-side on
 * start, so a failed banner lookup must never invent a lock the API would not
 * apply.
 *
 * @param {string} studentId
 * @returns {{subscription: object, isLoading: boolean}}
 */
const useSubscriptionStatus = studentId => {
  const [subscription, setSubscription] = useState(UNLOCKED)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!studentId) return undefined

    let isCurrent = true
    const fetchStatus = async () => {
      setIsLoading(true)
      try {
        const res = await studentRequest.get(
          `students/subscription/status/${studentId}`
        )
        if (isCurrent) setSubscription(res?.data?.data || UNLOCKED)
      } catch (error) {
        console.warn(
          '[Assessments] subscription status lookup failed:',
          error?.message
        )
        if (isCurrent) setSubscription(UNLOCKED)
      } finally {
        if (isCurrent) setIsLoading(false)
      }
    }

    fetchStatus()
    // Guards against a late response from a previous studentId overwriting the
    // current one.
    return () => {
      isCurrent = false
    }
  }, [studentId])

  return { subscription, isLoading }
}

export default useSubscriptionStatus
